import {
  styled,
  Box,
  Typography,
  Button,
  Select,
  TextField,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

export const FormContainer = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflow: 'hidden',
  position: 'relative',
});

export const ScrollableContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingTop: '2px',
  paddingBottom: 16,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
  },
});

export const Container = styled(Box)({
  display: 'flex',
  gap: '24px',
  flex: 1,
  overflow: 'hidden',
  paddingBottom: '50px',
});

export const LeftColumn = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
  width: '100%',
});

export const HeaderRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  marginBottom: 8,
});

export const LabelTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: '#555555',
  flex: '1 1 150px',
  minWidth: '150px',
});

export const Spacer24 = styled(Box)({
  width: '24px',
});

export const ItemCard = styled(Box)({
  border: '1px solid #D9F1FF',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  borderRadius: '8px',
  padding: '12px',
  paddingBottom: '16px',
  position: 'relative',
  marginBottom: 8,
});

export const SurchargeItemsWrapper = styled(Box)({
  flex: 1,
  minHeight: 100,
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
  },
});

export const ItemRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const SelectBox = styled(Box)({
  flex: '1 1 150px',
  minWidth: '150px',
  position: 'relative',
});

export const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'hasValue' && prop !== 'hasError',
})<{ hasValue?: boolean; hasError?: boolean }>(({ hasValue, hasError }) => ({
  width: '100%',
  height: '40px',
  backgroundColor: '#FCFEFF',
  borderRadius: '8px',
  '& .MuiSelect-select': {
    padding: '10px 16px',
    fontFamily: 'Outfit',
    fontSize: '14px',
    fontWeight: 400,
    color: hasValue ? '#0B2330' : '#75838A',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? '#FF0000' : '#D9F1FF',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0F52BA',
  },
  '& .MuiSvgIcon-root': {
    color: '#555555',
  },
}));

export const StyledAutocomplete = styled(Autocomplete)({
  width: '100%',
  '& .MuiAutocomplete-listbox': {
    maxHeight: '350px',
    overflow: 'auto',
  },
}) as typeof Autocomplete;

export const StyledAutocompleteTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasValue' && prop !== 'hasError',
})<{ hasValue?: boolean; hasError?: boolean }>(({ hasValue, hasError }) => ({
  '& .MuiOutlinedInput-root': {
    height: '40px',
    fontSize: '14px',
    fontFamily: 'Outfit',
    borderRadius: '8px',
    backgroundColor: '#FCFEFF',
    '& fieldset': {
      borderColor: hasError ? '#FF0000' : hasValue ? '#0099FF' : '#D9F1FF',
    },
    '&:hover fieldset': {
      borderColor: hasError ? '#FF0000' : '#0099FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? '#FF0000' : '#0099FF',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F5F5F5',
    },
  },
  '& .MuiInputBase-input': {
    padding: '0 14px !important',
    fontSize: '14px',
    fontFamily: 'Outfit',
    fontWeight: 400,
    color: hasValue ? '#0B2330' : '#75838A',
    '&::placeholder': {
      color: '#75838A',
      opacity: 1,
    },
  },
}));

export const ErrorTextSelect = styled(Typography)({
  color: '#FF0000',
  fontSize: '12px',
  position: 'absolute',
  left: 0,
  top: '40px',
});

export const AmountBox = styled(Box)({
  flex: '1 1 150px',
  minWidth: '150px',
  position: 'relative',
});

export const StyledTextField = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    backgroundColor: '#FCFEFF',
    borderRadius: '8px',
    fontFamily: 'Outfit',
    fontSize: '14px',
    '& fieldset': {
      borderColor: '#D9F1FF',
    },
    '&:hover fieldset': {
      borderColor: '#0F52BA',
    },
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
});

export const ErrorTextAmount = styled(Typography)({
  color: '#FF0000',
  fontSize: '12px',
  position: 'absolute',
  left: 0,
  top: '40px',
});

export const DeleteButton = styled(IconButton)({
  alignSelf: 'flex-end',
  marginBottom: '8px',
  width: '24px',
  height: '24px',
});

export const StyledDeleteIcon = styled(DeleteIcon)({
  color: '#75838A',
  fontSize: '16px',
});

export const AddButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  textTransform: 'none',
  backgroundColor: 'transparent',
  color: '#0F52BA',
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  padding: 0,
  minWidth: 'auto',
  width: 'fit-content',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  flexShrink: 0,
});

export const AddSurchargeButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  position: 'sticky',
  bottom: 0,
  backgroundColor: '#FFFFFF',
  paddingTop: '8px',
  paddingBottom: '8px',
  zIndex: 1,
});

export const StyledAddIcon = styled(AddIcon)({
  fontSize: '24px',
});

export const RightPanel = styled(Box)({
  flex: 1,
  width: '100%',
  backgroundColor: '#FAFAFA',
  height: '300px',
  borderRadius: '8px',
  padding: '12px 12px 12px 12px',
  position: 'relative',
  boxSizing: 'border-box',
});

export const ContentWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
});

export const FinalTotalsTitle = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: '#75838A',
  marginBottom: '19.63px',
});

export const TotalsRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '12px',
});

export const TotalLabel = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: '#0B2330',
});

export const TotalValue = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '16px',
  color: '#0B2330',
  textAlign: 'right',
});

export const SurchargeRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '23.73px',
});

export const GrandTotalBox = styled(Box)({
  backgroundColor: '#FFFFFF',
  border: '1px solid #D9F1FF',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 12px',
});

export const NextButtonContainer = styled(Box)({
  flexShrink: 0,
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  paddingTop: 8,
  paddingLeft: '24px',
  paddingRight: '24px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '16px',
  zIndex: 10,
});

export const BackButton = styled(Button)({
  width: '82px',
  height: '40px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  boxShadow: '0px 2px 4px #E2E3E1',
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontSize: '16px',
  color: '#0B2330',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
});

export const NextButton = styled(Button)({
  width: '82px',
  height: '40px',
  backgroundColor: '#0F52BA',
  border: '1px solid #0D4AA9',
  borderRadius: '8px',
  boxShadow: '0px 2px 4px #E2E3E1',
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontSize: '16px',
  color: '#FFFFFF',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#0D4AA9',
  },
});

export const DiscountLabelRed = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '16px',
  color: '#e85e51',
});

export const DiscountRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '16px',
});
